import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Button } from '@/Components/ui/button';
import { Heading, Text } from '@/Components/ui/typography';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';
import { acceptInvite, previewInvite } from '../Settings/teamApi';

const MIN_PASSWORD_LENGTH = 8;

const passwordProblem = (password, confirmPassword) => {
  if (password.length < MIN_PASSWORD_LENGTH || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters with both letters and numbers.`;
  }
  if (password !== confirmPassword) return "Those passwords don't match.";
  return '';
};

const primaryButton = {
  backgroundColor: getColor('primary'),
  color: getColor('primaryForeground'),
  borderColor: getColor('primary'),
};

const Shell = ({ children }) => (
  <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-background px-4 py-16 text-foreground">
    <div className="absolute left-6 top-6 md:left-8 md:top-8">
      <Link to="/hirewithus" className="transition-opacity hover:opacity-80">
        <img src="/Newzenalphablue.png" alt="SearchJob.ai Logo" className="h-8 w-auto object-contain md:h-10" />
      </Link>
    </div>
    <Card className={cn('flex w-full max-w-[440px] flex-col p-8', 'border border-border/50 bg-card shadow-xl shadow-primary/5')}>
      <CardContent className="w-full space-y-5 p-0">{children}</CardContent>
    </Card>
  </div>
);

/** /hirewithus/accept-invite/:token — where a person invited to a company's team
 *  sets their password. Public: the link is the credential. */
function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [checking, setChecking] = useState(true);
  const [linkProblem, setLinkProblem] = useState('');

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const details = await previewInvite(token);
        if (!active) return;
        setInvite(details);
        setFullName(details.full_name || '');
      } catch (err) {
        if (!active) return;
        setLinkProblem(
          err?.response?.status === 410
            ? err.response.data?.error || 'This invite link no longer works.'
            : "We couldn't open this invite. Check your connection and try again."
        );
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!fullName.trim()) {
      setError('Enter your name.');
      return;
    }
    const problem = passwordProblem(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await acceptInvite({ token, password, fullName: fullName.trim() });
      navigate('/hirewithus/login', {
        replace: true,
        state: { email: invite.email, notice: 'Your account is ready. Sign in to get started.' },
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Checking your invite" />
        </div>
      </Shell>
    );
  }

  if (linkProblem) {
    return (
      <Shell>
        <Heading level="h1" className="text-2xl font-bold tracking-tight">This invite can't be used</Heading>
        <Text size="base" variant="muted">{linkProblem}</Text>
        <Link to="/hirewithus/login" className="text-sm font-medium text-primary hover:underline">
          Go to sign in
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-1.5">
        <Heading level="h1" className="text-2xl font-bold tracking-tight">Join {invite.company_name}</Heading>
        <Text size="sm" variant="muted">
          You're invited as {invite.role_name}. Set a password to finish.
        </Text>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-email" className="text-sm font-semibold">Email</Label>
          <Input id="invite-email" className="h-10" value={invite.email} readOnly disabled />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-name" className="text-sm font-semibold">Your name</Label>
          <Input id="invite-name" className="h-10" autoComplete="name" value={fullName} disabled={saving}
            onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-password" className="text-sm font-semibold">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input id="invite-password" type="password" className="h-10 pl-10" autoComplete="new-password"
              value={password} disabled={saving} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">
            At least {MIN_PASSWORD_LENGTH} characters with letters and numbers.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-confirm" className="text-sm font-semibold">Confirm password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input id="invite-confirm" type="password" className="h-10 pl-10" autoComplete="new-password"
              value={confirmPassword} disabled={saving} onChange={(e) => setConfirmPassword(e.target.value)} />
          </div>
        </div>

        {error ? (
          <div role="alert" className="rounded-md border border-destructive/20 bg-destructive/10 p-3">
            <Text size="sm" className="text-destructive">{error}</Text>
          </div>
        ) : null}

        <Button type="submit" className="h-11 w-full font-semibold" style={primaryButton} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Create my account
        </Button>
      </form>
    </Shell>
  );
}

export default AcceptInvite;
